/*!
 * CHIEF - Cybersecurity Learning Platform (PROPRIETARY SOFTWARE)
 * Registration Page - CHIEF Platform
 * 
 * Copyright (c) 2025 Srivatsan Sk. All rights reserved.
 * 
 * PROPRIETARY LICENSE NOTICE:
 * This software is the exclusive proprietary property of Srivatsan Sk.
 * Licensed to client under strict commercial license agreement.
 * Unauthorized use, copying, modification, distribution, or removal
 * of copyright notices is strictly prohibited.
 * 
 * Original Author: Srivatsan Sk
 * License: Proprietary/Commercial
 * Status: Confidential
 * 
 * WARNING: This code contains proprietary business logic and algorithms.
 * Reverse engineering, decompilation, or derivative works are prohibited.
 */

// src/pages/Register.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiEye, FiEyeOff, FiUser, FiMail, FiLock, FiShield } from 'react-icons/fi';
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { useAuth } from '../contexts/AuthContext';
import { validatePassword, validateConfirmPassword as validateConfirmPasswordUtil } from '../utils/passwordValidation';

const RegisterContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing[4]};
  position: relative;
  overflow: hidden;
`;

const ParticlesContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing[8]};
  padding: ${({ theme }) => theme.spacing[6]};
  background: rgba(0, 210, 255, 0.05);
  backdrop-filter: blur(5px);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid rgba(0, 210, 255, 0.1);
  max-width: 600px;
  width: 100%;
`;

const Logo = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes['4xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  letter-spacing: 1px;
  text-shadow: 0 0 20px rgba(0, 210, 255, 0.5);
`;

const TagLine = styled.p`
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  font-weight: 300;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]};
  background: rgba(0, 210, 255, 0.03);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid rgba(0, 210, 255, 0.08);
  
  svg {
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: ${({ theme }) => theme.spacing[2]};
  }
  
  span {
    font-size: ${({ theme }) => theme.fonts.sizes.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
    text-align: center;
  }
`;

const RegisterCard = styled.div`
  width: 100%;
  max-width: 600px;
  background: rgba(18, 24, 38, 0.85);
  backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[8]};
  position: relative;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.medium}, 0 8px 32px rgba(0, 210, 255, 0.1);
  border: 1px solid rgba(0, 210, 255, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme }) => theme.colors.gradientPrimary};
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  font-weight: 600;
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.primary};
`;

const Subtitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing[5]};
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  font-weight: 500;
  
  .required {
    color: ${({ theme }) => theme.colors.primary};
    margin-left: 2px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.glow};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%238A8A9D' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.glow};
  }
`;

const RegisterButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  transition: all ${({ theme }) => theme.transitions.fast};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const LoginLink = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  a {
    color: ${({ theme }) => theme.colors.primary};
    margin-left: ${({ theme }) => theme.spacing[1]};
    font-weight: 500;
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(255, 58, 94, 0.1);
  color: ${({ theme }) => theme.colors.error};
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  text-align: center;
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${({ theme }) => theme.spacing[3]};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const SuccessMessage = styled.div`
  background-color: rgba(0, 200, 83, 0.1);
  color: ${({ theme }) => theme.colors.success || '#00c853'};
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  text-align: center;
`;

const FieldError = styled.div`
  color: ${({ theme }) => theme.colors.error || '#ef4444'};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  margin-top: ${({ theme }) => theme.spacing[1]};
  display: flex;
  align-items: center;
  gap: 4px;
  
  &::before {
    content: '⚠️';
    font-size: 12px;
  }
`;

const PasswordRequirements = styled.div`
  margin-top: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const RequirementItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  
  &::before {
    content: ${({ met }) => met ? '✅' : '❌'};
    font-size: 10px;
  }
  
  color: ${({ met, theme }) =>
    met ? (theme.colors.success || '#10B981') : (theme.colors.textMuted || '#6B7280')};
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ValidatedInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border: 1px solid ${({ theme, hasError }) =>
    hasError ? theme.colors.error || '#ef4444' : theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme, hasError }) =>
    hasError ? theme.colors.error || '#ef4444' : theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme, hasError }) =>
    hasError ? 'rgba(239, 68, 68, 0.2)' : theme.colors.glow};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
  
  ${({ hasError, theme }) => hasError && `
    background-color: rgba(239, 68, 68, 0.05);
  `}
`;

const ResponseMessage = styled.div`
  background-color: rgba(59, 130, 246, 0.1);
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  text-align: center;
  word-break: break-word;
`;

const Footer = styled.footer`
  margin-top: ${({ theme }) => theme.spacing[8]};
  padding: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  text-align: center;
  background: rgba(0, 210, 255, 0.02);
  backdrop-filter: blur(5px);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid rgba(0, 210, 255, 0.05);
  max-width: 600px;
  width: 100%;
`;

const ValidationMessage = styled.div`
  margin-top: 4px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &.success {
    color: #28a745;
  }
  
  &.error {
    color: #dc3545;
  }
  
  &.validating {
    color: #6c757d;
  }
`;

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    college: '',
    registerNumber: '',
    phoneNumber: '',
    branch: '',
    semester: '',
    workshopCode: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [serverResponse, setServerResponse] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validation states
  const [validationErrors, setValidationErrors] = useState({});
  const [fieldTouched, setFieldTouched] = useState({});

  // Validation functions
  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validatePhoneNumber = (phone) => {
    if (!phone) return 'Phone number is required';
    // Remove all spaces, dashes, and plus signs for validation
    const cleanPhone = phone.replace(/[\s\-\+\(\)]/g, '');

    // Must be exactly 10 digits
    if (cleanPhone.length !== 10) {
      return 'Phone number must be exactly 10 digits';
    }

    // Must contain only digits
    if (!/^\d{10}$/.test(cleanPhone)) {
      return 'Phone number must contain only digits';
    }

    // Indian mobile number validation (10 digits starting with 6-9)
    const indianMobileRegex = /^[6-9]\d{9}$/;

    if (!indianMobileRegex.test(cleanPhone)) {
      return 'Phone number must start with 6, 7, 8, or 9 followed by 9 digits';
    }

    return '';
  };

  // Format phone number as user types (10 digits only)
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/[^\d]/g, '');

    // Limit to 10 digits only
    const limited = cleaned.substring(0, 10);

    // Format as XXXXX XXXXX (5-5 format)
    if (limited.length <= 5) {
      return limited;
    } else {
      return `${limited.substring(0, 5)} ${limited.substring(5, 10)}`;
    }
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    return validateConfirmPasswordUtil(confirmPassword, password);
  };

  const validateFullName = (name) => {
    if (!name) return 'Full name is required';
    if (name.length < 2) {
      return 'Name must be at least 2 characters long';
    }
    if (!/^[a-zA-Z\s]+$/.test(name)) {
      return 'Name can only contain letters and spaces';
    }
    return '';
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        return validateEmail(value);
      case 'phoneNumber':
        return validatePhoneNumber(value);
      case 'password':
        return validatePassword(value);
      case 'confirmPassword':
        return validateConfirmPassword(value, formData.password);
      case 'fullName':
        return validateFullName(value);
      default:
        return '';
    }
  };

  // Password strength checker
  const getPasswordRequirements = (password) => {
    return {
      minLength: password.length >= 8,
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>\/?]/.test(password),
    };
  };

  const isPasswordFocused = fieldTouched.password || formData.password.length > 0;

  // Hidden development signature - Srivatsan Sk
  React.useEffect(() => {
    if (typeof window !== 'undefined' && !window.__DEV_SIGNATURE) {
      window.__DEV_SIGNATURE = 'Srivatsan Sk';
      console.log('%c📝 Registration Module | Built by Srivatsan Sk', 'color: #4CAF50; font-size: 12px; font-weight: bold;');
    }
  }, []);

  // Initialize particles
  const particlesInit = useCallback(async engine => {
    await loadSlim(engine);
  }, []);

  const particlesOptions = {
    background: {
      color: {
        value: "transparent",
      },
    },
    fpsLimit: 60,
    interactivity: {
      events: {
        onClick: {
          enable: true,
          mode: "push",
        },
        onHover: {
          enable: true,
          mode: "repulse",
        },
        resize: true,
      },
      modes: {
        push: {
          quantity: 2,
        },
        repulse: {
          distance: 80,
          duration: 0.4,
        },
      },
    },
    particles: {
      color: {
        value: "#00d2ff",
      },
      links: {
        color: "#00d2ff",
        distance: 120,
        enable: true,
        opacity: 0.15,
        width: 1,
      },
      collisions: {
        enable: true,
      },
      move: {
        direction: "none",
        enable: true,
        outModes: {
          default: "bounce",
        },
        random: false,
        speed: 0.8,
        straight: false,
      },
      number: {
        density: {
          enable: true,
          area: 1000,
        },
        value: 60,
      },
      opacity: {
        value: 0.25,
      },
      shape: {
        type: "circle",
      },
      size: {
        value: { min: 1, max: 2 },
      },
    },
    detectRetina: true,
  };

  const { register } = useAuth();

  // Function to display a global error or success message
  const showGlobalError = (message, type = 'error') => {
    // Remove existing error if any
    const existingError = document.getElementById('global-error-popup');
    if (existingError) {
      document.body.removeChild(existingError);
    }

    // Create new error popup element
    const errorPopup = document.createElement('div');
    errorPopup.id = 'global-error-popup';

    // Set colors based on message type
    const backgroundColor = type === 'success' ? '#28a745' : '#dc3545';
    const color = 'white';

    // Style the popup
    Object.assign(errorPopup.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      backgroundColor: backgroundColor,
      color: color,
      padding: '12px 20px',
      borderRadius: '6px',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
      zIndex: '9999',
      maxWidth: '350px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '14px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      opacity: '0',
      transform: 'translateX(30px)',
      transition: 'all 0.3s ease'
    });

    // Create close button
    const closeButton = document.createElement('span');
    closeButton.innerHTML = '&times;';
    Object.assign(closeButton.style, {
      marginLeft: '15px',
      fontSize: '20px',
      cursor: 'pointer',
      fontWeight: 'bold'
    });

    // Add close functionality
    closeButton.onclick = function () {
      errorPopup.style.opacity = '0';
      errorPopup.style.transform = 'translateX(30px)';
      setTimeout(() => {
        if (document.body.contains(errorPopup)) {
          document.body.removeChild(errorPopup);
        }
      }, 300);
    };

    // Set the message
    const messageSpan = document.createElement('span');
    messageSpan.innerText = message;
    errorPopup.appendChild(messageSpan);
    errorPopup.appendChild(closeButton);

    // Add to the DOM
    document.body.appendChild(errorPopup);

    // Trigger animation
    setTimeout(() => {
      errorPopup.style.opacity = '1';
      errorPopup.style.transform = 'translateX(0)';
    }, 10);

    // Auto dismiss after different times based on type
    const dismissTime = type === 'success' ? 7000 : 5000;
    setTimeout(() => {
      if (document.body.contains(errorPopup)) {
        errorPopup.style.opacity = '0';
        errorPopup.style.transform = 'translateX(30px)';
        setTimeout(() => {
          if (document.body.contains(errorPopup)) {
            document.body.removeChild(errorPopup);
          }
        }, 300);
      }
    }, dismissTime);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Format phone number as user types
    let processedValue = value;
    if (name === 'phoneNumber') {
      processedValue = formatPhoneNumber(value);
    }

    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Mark field as touched
    setFieldTouched(prev => ({
      ...prev,
      [name]: true
    }));

    // Validate field (for phone number, validate the cleaned version)
    let valueToValidate = processedValue;
    if (name === 'phoneNumber') {
      valueToValidate = processedValue.replace(/\s/g, ''); // Remove spaces for validation
    }

    const error = validateField(name, valueToValidate);
    setValidationErrors(prev => ({
      ...prev,
      [name]: error
    }));

    // Special case: re-validate confirm password when password changes
    if (name === 'password' && formData.confirmPassword && fieldTouched.confirmPassword) {
      const confirmPasswordError = validateConfirmPassword(formData.confirmPassword, processedValue);
      setValidationErrors(prev => ({
        ...prev,
        confirmPassword: confirmPasswordError
      }));
    }
  };

  // Handle field blur for validation
  const handleBlur = (e) => {
    const { name, value } = e.target;

    // Mark field as touched
    setFieldTouched(prev => ({
      ...prev,
      [name]: true
    }));

    // Validate field (for phone number, validate the cleaned version)
    let valueToValidate = value;
    if (name === 'phoneNumber') {
      valueToValidate = value.replace(/\s/g, ''); // Remove spaces for validation
    }

    const error = validateField(name, valueToValidate);
    setValidationErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset previous responses
    setServerResponse(null);

    // Mark all fields as touched for validation display
    const allFields = ['email', 'fullName', 'phoneNumber', 'password', 'confirmPassword'];
    const newTouched = {};
    allFields.forEach(field => {
      newTouched[field] = true;
    });
    setFieldTouched(newTouched);

    // Validate all required fields
    const newErrors = {};
    allFields.forEach(field => {
      let valueToValidate = formData[field];
      // For phone number, validate the cleaned version (without spaces)
      if (field === 'phoneNumber') {
        valueToValidate = formData[field] ? formData[field].replace(/\s/g, '') : '';
      }

      const error = validateField(field, valueToValidate);
      if (error) {
        newErrors[field] = error;
      }
    });

    setValidationErrors(newErrors);

    // Check if there are any validation errors
    if (Object.keys(newErrors).length > 0) {
      const firstError = Object.values(newErrors)[0];
      setError('Please correct the errors below');
      showGlobalError(`Validation Error: ${firstError}`);
      return;
    }

    // Additional validation checks
    if (!formData.email || !formData.fullName || !formData.password) {
      setError('Please fill in all required fields');
      showGlobalError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      showGlobalError('Passwords do not match');
      return;
    }

    await submitRegistration();
  };

  const submitRegistration = async () => {

    try {
      setError('');
      setLoading(true);

      const result = await register(formData);

      // Store the server response instead of redirecting
      setServerResponse(result);

      // Display error message if present in response
      if (result.error || result.detail || !result.success) {
        let errorMessage = result.detail ||
          (result.error?.message || result.error) ||
          'Registration failed';

        setError(errorMessage);
        showGlobalError(errorMessage);
        return; // Don't continue if there's an error
      }

      // Success case - display success message
      if (result.success && result.message) {
        showGlobalError(result.message, 'success');
      }

    } catch (err) {
      console.error("Registration error:", err);

      // Extract error message from error object
      let errorMessage = 'Registration failed';

      if (err && err.response && err.response.data) {
        const errorData = err.response.data;

        // Handle Django REST Framework validation errors (field-specific)
        if (typeof errorData === 'object' && !Array.isArray(errorData) && !errorData.detail && !errorData.message) {
          // This is likely a DRF validation error object like {"email": ["Error message"]}
          const errorMessages = [];
          Object.keys(errorData).forEach(field => {
            const fieldErrors = Array.isArray(errorData[field]) ? errorData[field] : [errorData[field]];
            fieldErrors.forEach(error => {
              errorMessages.push(`${field}: ${error}`);
            });
          });
          errorMessage = errorMessages.join(', ');
        }
        // Handle standard error responses
        else if (errorData.detail) {
          errorMessage = errorData.detail;
        }
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
        // Handle string responses
        else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
        // Handle non_field_errors (common in DRF)
        else if (errorData.non_field_errors && Array.isArray(errorData.non_field_errors)) {
          errorMessage = errorData.non_field_errors.join(', ');
        }
        // Fallback for complex objects
        else {
          errorMessage = JSON.stringify(errorData);
        }
      } else if (err && err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      showGlobalError(errorMessage);

      // Still store the error response
      setServerResponse(err.response?.data || { detail: errorMessage });

    } finally {
      setLoading(false);
    }
  };

  // Format server response for display
  const formatResponse = (response) => {
    if (!response) return null;

    try {
      if (typeof response === 'string') {
        return response;
      }
      return JSON.stringify(response, null, 2);
    } catch (e) {
      return String(response);
    }
  };

  return (
    <RegisterContainer>
      <ParticlesContainer>
        <Particles
          id="tsparticles-register"
          init={particlesInit}
          options={particlesOptions}
        />
      </ParticlesContainer>

      <ContentWrapper>
        <br></br>
        <Logo>C.H.I.E.F</Logo>



        <RegisterCard>
        <Title>New Identity Creation</Title>
        <Subtitle>Join our elite community of cyber security learners</Subtitle>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {serverResponse && (
          <ResponseMessage>
            <strong>Server Response:</strong>
            <pre style={{
              marginTop: '8px',
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: '14px'
            }}>
              {formatResponse(serverResponse)}
            </pre>
          </ResponseMessage>
        )}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">Email <span className="required">*</span></Label>
              <InputWrapper>
                <ValidatedInput
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasError={fieldTouched.email && validationErrors.email}
                  required
                />
                {fieldTouched.email && validationErrors.email && (
                  <FieldError>{validationErrors.email}</FieldError>
                )}
                {!validationErrors.email && formData.email && fieldTouched.email && (
                  <div style={{
                    fontSize: '12px',
                    color: '#10B981',
                    marginTop: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    ✅ Valid email format
                  </div>
                )}
              </InputWrapper>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="fullName">Full Name <span className="required">*</span></Label>
              <InputWrapper>
                <ValidatedInput
                  type="text"
                  id="fullName"
                  name="fullName"
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasError={fieldTouched.fullName && validationErrors.fullName}
                  required
                />
                {fieldTouched.fullName && validationErrors.fullName && (
                  <FieldError>{validationErrors.fullName}</FieldError>
                )}
              </InputWrapper>
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label htmlFor="college">College <span className="required">*</span></Label>
              <Input
                type="text"
                id="college"
                name="college"
                placeholder="Your college/university"
                value={formData.college}
                onChange={handleChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="registerNumber">Register Number <span className="required">*</span></Label>
              <Input
                type="text"
                id="registerNumber"
                name="registerNumber"
                placeholder="Your register number"
                value={formData.registerNumber}
                onChange={handleChange}
                required
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label htmlFor="phoneNumber">Phone Number <span className="required">*</span></Label>
                <InputWrapper>
                  <ValidatedInput
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    placeholder="Enter 10-digit mobile number (e.g., 9876543210)"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    hasError={fieldTouched.phoneNumber && validationErrors.phoneNumber}
                    required
                  />
                  {fieldTouched.phoneNumber && validationErrors.phoneNumber && (
                    <FieldError>{validationErrors.phoneNumber}</FieldError>
                  )}
                  {!validationErrors.phoneNumber && formData.phoneNumber && fieldTouched.phoneNumber && (
                    <div style={{
                      fontSize: '12px',
                      color: '#10B981',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      ✅ Valid 10-digit mobile number
                    </div>
                  )}
                </InputWrapper>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="branch">Branch <span className="required">*</span></Label>
              <Input
                type="text"
                id="branch"
                name="branch"
                placeholder="e.g. Computer Science"
                value={formData.branch}
                onChange={handleChange}
                required
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label htmlFor="semester">Semester <span className="required">*</span></Label>
            <Select
              id="semester"
              name="semester"
              value={formData.semester}
              onChange={handleChange}
              required
            >
              <option value="" disabled>Select your semester</option>
              <option value="1">1st Semester</option>
              <option value="2">2nd Semester</option>
              <option value="3">3rd Semester</option>
              <option value="4">4th Semester</option>
              <option value="5">5th Semester</option>
              <option value="6">6th Semester</option>
              <option value="7">7th Semester</option>
              <option value="8">8th Semester</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="workshopCode">Workshop Code (Optional)</Label>
            <Input
              type="text"
              id="workshopCode"
              name="workshopCode"
              placeholder="Enter workshop code if you have one"
              value={formData.workshopCode}
              onChange={handleChange}
            />
            <small style={{ color: '#888', fontSize: '14px', marginTop: '4px', display: 'block' }}>
              If you have a workshop code, enter it here to access workshop-specific courses
            </small>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password <span className="required">*</span></Label>
              <PasswordInputWrapper>
                <ValidatedInput
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  placeholder="Create a secure password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasError={fieldTouched.password && validationErrors.password}
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </PasswordToggle>
              </PasswordInputWrapper>
              {fieldTouched.password && validationErrors.password && (
                <FieldError>{validationErrors.password}</FieldError>
              )}
              {isPasswordFocused && (
                <PasswordRequirements>
                  <div style={{ marginBottom: '8px', fontWeight: '500', color: '#6B7280' }}>
                    Password Requirements:
                  </div>
                  <RequirementItem met={getPasswordRequirements(formData.password).minLength}>
                    At least 8 characters
                  </RequirementItem>
                  <RequirementItem met={getPasswordRequirements(formData.password).hasUpper}>
                    One uppercase letter
                  </RequirementItem>
                  <RequirementItem met={getPasswordRequirements(formData.password).hasLower}>
                    One lowercase letter
                  </RequirementItem>
                  <RequirementItem met={getPasswordRequirements(formData.password).hasNumber}>
                    One number
                  </RequirementItem>
                  <RequirementItem met={getPasswordRequirements(formData.password).hasSpecial}>
                    One special character (e.g. !@#$%)
                  </RequirementItem>
                </PasswordRequirements>
              )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm Password <span className="required">*</span></Label>
              <PasswordInputWrapper>
                <ValidatedInput
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasError={fieldTouched.confirmPassword && validationErrors.confirmPassword}
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  title={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </PasswordToggle>
              </PasswordInputWrapper>
              {fieldTouched.confirmPassword && validationErrors.confirmPassword && (
                <FieldError>{validationErrors.confirmPassword}</FieldError>
              )}
          </FormGroup>

          <RegisterButton type="submit" disabled={loading}>
            {loading ? 'Registering...' : 'Register'}
          </RegisterButton>

          <LoginLink>
            Already have an account?<Link to="/login">Login</Link>
          </LoginLink>
        </Form>
      </RegisterCard>

      <Footer>
        © {new Date().getFullYear()} C.H.I.E.F. All rights reserved.
      </Footer>
      </ContentWrapper>
    </RegisterContainer>
  );
};

export default Register;